import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import tr from "@/locales/tr/common.json";
import en from "@/locales/en/common.json";
import { logDev } from "@/lib/logger";

const DICTS = { tr, en };
const SUPPORTED = ["tr", "en"];
const DEFAULT = "tr";

const I18nContext = createContext(null);

function pickBrowserLocale() {
  const langs = (navigator.languages || [navigator.language || DEFAULT]);
  for (const l of langs) {
    const short = String(l).toLowerCase().split("-")[0];
    if (SUPPORTED.includes(short)) return short;
  }
  return DEFAULT;
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    return localStorage.getItem("acas_locale") || pickBrowserLocale();
  });

  useEffect(() => {
    localStorage.setItem("acas_locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = async (lc) => {
    if (!SUPPORTED.includes(lc)) return;
    setLocaleState(lc);
    // Best-effort persist to backend. We use raw axios (bypassing the app's
    // 401-redirect interceptor) so a guest toggling the language on /giris
    // does not get force-navigated to /giris again.
    //
    // A 401 for anonymous users is EXPECTED and swallowed silently. Any
    // other error is logged only in dev builds.
    try {
      const axios = (await import("axios")).default;
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/language`,
        { language: lc },
        { withCredentials: true, headers: { "Accept-Language": lc } },
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 401) logDev("i18n.setLocale", err);
    }
  };

  const t = useMemo(() => {
    const dict = DICTS[locale] || DICTS[DEFAULT];
    return (key, fallback) => {
      if (dict && Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
      // report missing key to console (once) to help future translation work
      if (fallback === undefined && typeof console !== "undefined") {
        // do not spam
      }
      return fallback !== undefined ? fallback : key;
    };
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, supported: SUPPORTED }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
